import * as flashActions from 'pkg/actions/flashes';
import type {
	CreatePayload,
	CreatePayloadV2,
	CreateV2Response,
	FormAgreementParams,
	FormFieldParams,
	PackageParams,
	PackageProductParams,
	PackagePaymentOptionParams,
	UpdateFormFieldParams,
	UpdateFormPayload,
	UpdatePayloadV2,
} from 'pkg/actions/forms/interface';

import * as endpoints from 'pkg/api/endpoints/auto';
import type * as models from 'pkg/api/models';
import { consentInteractionTypeFor } from 'pkg/api/models/agreement/utils';
import type {
	FormJoinMethod,
	FormTemplate,
	Statuses,
} from 'pkg/api/models/form';
import type { FormField } from 'pkg/api/models/form_field';
import type { FormPackagePaymentOption } from 'pkg/api/models/form_package_payment_option';
import * as sdk from 'pkg/core/sdk';
import { crash } from 'pkg/errors/errors';

import type { FormAgreement } from 'routes/forms/types';

import { Event, trackEvent } from 'components/analytics';

export function mapFieldToParams(field: FormField): FormFieldParams {
	return {
		key: field.key,
		type: field.type,
		label: field.label,
		description: field.description || '',
		values: field.values
			? field.values.map((value) => ({
					value: value.value || '',
					label: value.label || '',
				}))
			: [],
		options: field.options
			? field.options.map((option, index) => ({
					value: option.value || '',
					label: option.label || '',
					sortOrder: index,
					maxSubmissions: option.maxSubmissions,
					tempId: option.tempId,
				}))
			: [],
		sortOrder: field.sortOrder || 0,
		required: field.required || false,
		userFieldId: field.userFieldId,
		attachmentType: field.attachmentType,
		displayAsDropdown: field.displayAsDropdown || false,
		tempId: field.tempId || undefined,
	};
}

export function mapFieldToUpdateParams(
	field: FormField
): UpdateFormFieldParams {
	return {
		id: field.id,
		key: field.key,
		type: field.type,
		label: field.label,
		description: field.description || '',
		values: field.values?.map((value) => ({
			value: value.value || '',
			label: value.label || '',
		})),
		options: field.options
			? field.options.map((option, index) => ({
					id: option.id,
					value: option.value || '',
					label: option.label || '',
					sortOrder: index,
					maxSubmissions: option.maxSubmissions,
					tempId: option.tempId,
				}))
			: [],
		sortOrder: field.sortOrder || 0,
		required: field.required || false,
		userFieldId: field.userFieldId,
		attachmentType: field.attachmentType,
		displayAsDropdown: field.displayAsDropdown || false,
		tempId: field.tempId || undefined,
	};
}

export function mapAgreementsToParams(
	agreements: FormAgreement[]
): FormAgreementParams[] {
	return agreements.map((agreement) => ({
		agreementId: agreement.id,
		consentInteractionType: consentInteractionTypeFor(
			agreement.consentRequired
		),
	}));
}

export function mapAgreementLinksToParams(
	links: models.agreement.FormAgreementLink[]
): FormAgreementParams[] {
	return links.map((link) => ({
		agreementId: link.agreementId,
		consentInteractionType: link.consentInteractionType,
	}));
}

export function mapPaymentOptionParams(
	paymentOptions: FormPackagePaymentOption[]
): PackagePaymentOptionParams[] {
	return paymentOptions.map((pkgOption, index) => {
		const param: PackagePaymentOptionParams = {
			id: pkgOption.id ?? undefined,
			title: pkgOption.title,
			sortOrder: index,
			productPriceIds: pkgOption.prices.map((price) => price.productPriceId),
			installmentCount: pkgOption.installmentCount,
			payFirstOrderNow: false,
			validUntil: pkgOption.validUntil,
			validFrom: pkgOption.validFrom,
			isDuplicate: pkgOption.isDuplicate,
		};

		if (pkgOption.subscriptionStartAt) {
			param.subscriptionStartAt = pkgOption.subscriptionStartAt;
			param.payFirstOrderNow = true;
		}

		return param;
	});
}

export const update = async (
	organizationId: number,
	formId: number,
	payload: UpdateFormPayload
): Promise<Response> => {
	const request = await sdk.patch(
		endpoints.Forms.Update(organizationId, formId),
		{},
		payload
	);
	const response = await request.json();

	if (!request.ok) {
		flashActions.show({
			title: crash().title,
			message: response.error,
		});

		return;
	}

	return request;
};

/** The data a duplicate can leave out, as the API's `omit` takes it. */
export type DuplicateOmission =
	| 'event_groups'
	| 'team_invitation_rules'
	| 'form_field_rules'
	| 'target_groups';

export interface DuplicatePayload {
	title: string;
	status: Statuses;
	template?: FormTemplate;
	/** `"invalid"` clears the join method, as on the v2 update. */
	joinMethod?: FormJoinMethod | 'invalid';
	inviteOnly?: boolean;
	forceAuth?: boolean;
	omit?: DuplicateOmission[];
}

export type DuplicatedForm = models.form.Form & { pricesSkipped: boolean };

export const duplicate = async (
	organizationId: number,
	id: number,
	payload: DuplicatePayload
): Promise<[boolean, DuplicatedForm | null]> => {
	const request = await sdk.post(
		endpoints.Forms.Duplicate(organizationId, id),
		{},
		payload
	);
	const response = await request.json();

	if (!request.ok) {
		flashActions.show({
			title: crash().title,
			message: response.error,
		});

		return [false, null];
	}

	return [true, response];
};

export const create = async (
	organizationId: number,
	payload: CreatePayload
): Promise<[boolean, models.form.Form]> => {
	const request = await sdk.post(
		endpoints.Forms.Create(organizationId),
		{},
		payload
	);
	const response = await request.json();
	if (!request.ok) {
		flashActions.show({
			title: crash().title,
			message: response.error,
		});

		return [false, null];
	}

	trackEvent(Event.RegistrationFormCreated, { form_id: response.guid });

	return [true, response];
};

export async function createV2(
	organizationId: number,
	payload: CreatePayloadV2
): Promise<[boolean, CreateV2Response]> {
	const request = await sdk.post(
		endpoints.Forms.CreateV2(organizationId),
		{},
		payload
	);
	const response = await request.json();

	if (!request.ok) {
		flashActions.show({
			title: crash().title,
			message: response.error,
		});

		return [false, null];
	}

	trackEvent(Event.RegistrationFormCreated, { form_id: response.form.guid });

	return [true, response];
}

export function formToUpdateV2Payload(form: models.form.Form): UpdatePayloadV2 {
	return {
		title: form.title,
		description: form.description ?? '',
		formCategoryId: form.formCategoryId,
		attachmentId: form.attachment?.id,
		status: form.status,
		visibility: form.visibility,
		forceAuth: form.forceAuth ?? false,
		maxSubmissions: form.maxSubmissions,
		maxSubmissionsPerUser: form.maxSubmissionsPerUser,
		submissionEmailEnabled: form.submissionEmailEnabled,
		submissionEmailSubject: form.submissionEmailSubject ?? '',
		submissionEmailContent: form.submissionEmailContent ?? '',
		openSubmissionsAt: form.openSubmissionsAt,
		closeSubmissionsAt: form.closeSubmissionsAt,
		inviteOnly: form.inviteOnly ?? false,
		nonEligibleMessage: form.nonEligibleMessage ?? '',
		parentFormId: form.parentFormId,
	};
}

export async function updateV2(
	organizationId: number,
	formId: number,
	payload: UpdatePayloadV2
): Promise<[boolean, CreateV2Response]> {
	const request = await sdk.patch(
		endpoints.Forms.UpdateV2(organizationId, formId),
		{},
		payload
	);
	const response = await request.json();

	if (!request.ok) {
		flashActions.show({
			title: crash().title,
			message: response.error,
		});

		return [false, null];
	}

	return [true, response];
}

/**
 * Switches a team registration's join method. Blocked server-side once the form has any
 * submission or invite — callers must call this *before* applying the matching package (and
 * field) split/merge, and only do so once this call succeeds, since the packages upsert endpoint
 * drops anything not in the list it's given and would otherwise reshape (or delete) packages a
 * blocked switch should have left untouched.
 */
export async function updateJoinMethod(
	organizationId: number,
	form: models.form.Form,
	joinMethod: models.form.FormJoinMethod
): Promise<[boolean, CreateV2Response]> {
	return updateV2(organizationId, form.id, {
		...formToUpdateV2Payload(form),
		joinMethod,
	});
}

export async function upsertFields(
	organizationId: number,
	formId: number,
	fields: FormField[]
): Promise<[boolean, { formFields: FormField[] } | null]> {
	const request = await sdk.put(
		endpoints.FormFields.Upsert(organizationId, formId),
		{},
		// Use the update mapper so existing option IDs are preserved. Without them the API can't
		// match options (it matches by ID), recreates every option with a new ID on each save, and
		// orphans any package eligibility rule referencing the old IDs. See REG-825.
		{ fields: fields.map(mapFieldToUpdateParams) }
	);
	const response = await request.json();

	if (!request.ok) {
		flashActions.show({
			title: crash().title,
			message: response.error,
		});

		return [false, null];
	}

	return [true, response];
}

export function mapPackageToParams(
	pkg: models.formPackage.FormPackage
): PackageParams {
	const products: PackageProductParams[] = pkg.products.map((prod) => ({
		productId: prod.productId,
		quantity: prod.quantity,
		required: prod.required,
		validTo: prod.validTo,
		minQuantity: prod.minQuantity,
		maxQuantity: prod.maxQuantity,
		taxRateId: prod.taxRateId,
	}));

	return {
		id: pkg.id,
		title: pkg.name,
		description: pkg.description,
		products,
		sortOrder: pkg.sortOrder,
		targetGroupId: pkg.targetGroupId,
		paymentOptions: mapPaymentOptionParams(pkg.paymentOptions),
		rules: pkg.rules,
		packageEmailSubject: pkg.packageEmailSubject,
		packageEmailContent: pkg.packageEmailContent,
		packageManagers: pkg.packageManagers?.map((manager) => ({
			userId: manager.userId,
		})),
	};
}

export async function upsertAgreements(
	organizationId: number,
	formId: number,
	links: models.agreement.FormAgreementLink[]
): Promise<
	[boolean, { formAgreements: models.agreement.FormAgreementLink[] } | null]
> {
	const request = await sdk.put(
		endpoints.FormAgreements.Upsert(organizationId, formId),
		{},
		{ agreements: mapAgreementLinksToParams(links) }
	);
	const response = await request.json();

	if (!request.ok) {
		flashActions.show({
			title: crash().title,
			message: response.error,
		});

		return [false, null];
	}

	return [true, response];
}

export async function upsertPackages(
	organizationId: number,
	formId: number,
	packages: models.formPackage.FormPackage[]
): Promise<
	[boolean, { formPackages: models.formPackage.FormPackage[] } | null]
> {
	const request = await sdk.put(
		endpoints.FormPackages.Upsert(organizationId, formId),
		{},
		{ packages: packages.map(mapPackageToParams) }
	);
	const response = await request.json();

	if (!request.ok) {
		flashActions.show({
			title: crash().title,
			message: response.error,
		});

		return [false, null];
	}

	return [true, response];
}
